import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";

const Lightbox = ({ photos, index, onClose, onNavigate }) => {
  const photo = index !== null ? photos[index] : null;

  const prev = useCallback(
    () => onNavigate((index - 1 + photos.length) % photos.length),
    [index, photos.length, onNavigate]
  );
  const next = useCallback(
    () => onNavigate((index + 1) % photos.length),
    [index, photos.length, onNavigate]
  );

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, onClose, prev, next]);

  return (
    <AnimatePresence>
      {photo && (
        <motion.div
          data-testid="lightbox-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-50 flex flex-col bg-[#0A0A0A]/95 backdrop-blur-sm"
          onClick={onClose}
        >
          <div className="flex items-center justify-end px-6 py-6 md:px-12">
            <button
              data-testid="lightbox-close-button"
              onClick={onClose}
              aria-label="Close"
              className="text-[#F5F1EA] transition-opacity duration-300 hover:opacity-50"
            >
              <X size={22} strokeWidth={1.25} />
            </button>
          </div>

          <div
            className="flex flex-1 items-center justify-center px-6 md:px-24"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.img
              key={photo.id}
              data-testid="lightbox-image"
              src={photo.src}
              alt={photo.title}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[74vh] max-w-full object-contain"
            />
          </div>

          <div
            className="flex items-center justify-between px-6 py-8 md:px-12"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="lightbox-prev-button"
              onClick={prev}
              aria-label="Previous photograph"
              className="text-[#F5F1EA] transition-opacity duration-300 hover:opacity-50"
            >
              <ArrowLeft size={22} strokeWidth={1.25} />
            </button>
            <p className="font-body text-sm italic text-[#F5F1EA]/70">
              {String(index + 1).padStart(3, "0")}
            </p>
            <button
              data-testid="lightbox-next-button"
              onClick={next}
              aria-label="Next photograph"
              className="text-[#F5F1EA] transition-opacity duration-300 hover:opacity-50"
            >
              <ArrowRight size={22} strokeWidth={1.25} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
